import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { MessageSquare, Sparkles, ChevronRight } from 'lucide-react';
import { TranscriptChat } from './TranscriptChat';
import { TranscriptChatSheet } from './TranscriptChatSheet';
interface TranscriptContext {
  content: string;
  title?: string;
  description?: string;
  channel_name?: string;
  platform?: string;
  view_count?: number;
  like_count?: number;
  comment_count?: number;
  repost_count?: number;
  upload_date?: string;
  duration?: number;
  quote?: string;
  tldr?: string[];
  top_comments?: Array<{
    text: string;
    likes: number;
  }>;
}
interface TranscriptChatWidgetProps {
  transcript: TranscriptContext;
  transcriptId: string;
}
// Content-type specific example questions
const EXAMPLE_QUESTIONS = ['Break this down simply', 'What are the key points?', 'Quiz me on this', "Explain like I'm 5"];
// Helper to load message count from storage
const getMessageCount = (transcriptId: string): number => {
  try {
    const key = `transcript-chat-${transcriptId}`;
    const stored = localStorage.getItem(key);
    if (!stored) return 0;
    const data = JSON.parse(stored);
    return data.messages?.length || 0;
  } catch {
    return 0;
  }
};
// Helper to get last message preview
const getLastMessagePreview = (transcriptId: string): string | null => {
  try {
    const key = `transcript-chat-${transcriptId}`;
    const stored = localStorage.getItem(key);
    if (!stored) return null;
    const data = JSON.parse(stored);
    const messages = data.messages || [];
    if (messages.length === 0) return null;
    // Get last assistant message
    const lastAssistant = [...messages].reverse().find((m: any) => m.role === 'assistant');
    if (!lastAssistant) return null;
    // Truncate to 60 characters
    const preview = lastAssistant.content.substring(0, 60);
    return preview.length < lastAssistant.content.length ? `${preview}...` : preview;
  } catch {
    return null;
  }
};
export const TranscriptChatWidget: React.FC<TranscriptChatWidgetProps> = ({
  transcript,
  transcriptId
}) => {
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [isDesktopExpanded, setIsDesktopExpanded] = useState(false);
  const [initialQuestion, setInitialQuestion] = useState<string | null>(null);
  const [messageCount, setMessageCount] = useState(0);
  const [lastPreview, setLastPreview] = useState<string | null>(null);
  // Check for existing conversation on mount and when sheet closes
  useEffect(() => {
    const count = getMessageCount(transcriptId);
    const preview = getLastMessagePreview(transcriptId);
    setMessageCount(count);
    setLastPreview(preview);
  }, [transcriptId, isSheetOpen]);
  const handleWidgetClick = (question?: string) => {
    if (question) {
      setInitialQuestion(question);
    }
    const isMobile = window.innerWidth < 768;
    if (isMobile) {
      setIsSheetOpen(true);
    } else {
      setIsDesktopExpanded(true);
    }
  };
  const handleSheetClose = () => {
    setIsSheetOpen(false);
    setInitialQuestion(null);
    // Refresh message count after closing
    const count = getMessageCount(transcriptId);
    const preview = getLastMessagePreview(transcriptId);
    setMessageCount(count);
    setLastPreview(preview);
  };
  const hasConversation = messageCount > 0;
  return <>
      {/* Desktop: Inline expansion */}
      <div className="hidden md:block" data-id="element-1729">
        {!isDesktopExpanded ? <Card className="cursor-pointer hover:border-primary/50 hover:shadow-md transition-all" onClick={() => handleWidgetClick()} data-id="element-1730">
            <CardContent className="p-6" data-id="element-1731">
              <div className="flex items-center justify-between mb-4" data-id="element-1732">
                <h3 className="font-semibold text-lg flex items-center gap-2" data-id="element-1733">
                  <MessageSquare size={20} className="text-primary" data-id="element-1734" />
                  Chat about this transcript
                  {hasConversation && <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full" data-id="element-1735">
                      {messageCount} messages
                    </span>}
                </h3>
                <div className="flex items-center gap-1 text-xs text-muted-foreground bg-primary/10 px-2 py-1 rounded-full" data-id="element-1736">
                  <Sparkles size={12} className="text-primary" data-id="element-1737" />
                  <span data-id="element-1738">AI-powered</span>
                </div>
              </div>

              {hasConversation && lastPreview ? <div className="mb-4 p-3 bg-secondary/30 rounded-lg border border-border/40" data-id="element-1739">
                  <p className="text-xs text-muted-foreground mb-1" data-id="element-1740">
                    Last message:
                  </p>
                  <p className="text-sm line-clamp-2" data-id="element-1741">{lastPreview}</p>
                </div> : <p className="text-sm text-muted-foreground mb-4" data-id="element-1742">
                  Ask questions and get instant answers about this video
                </p>}

              <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide" data-id="element-1743">
                {EXAMPLE_QUESTIONS.map((question, index) => <Button key={index} variant="outline" size="sm" onClick={e => {
              e.stopPropagation();
              handleWidgetClick(question);
            }} className="text-xs rounded-full hover:bg-primary/10 hover:text-primary hover:border-primary/50 whitespace-nowrap flex-shrink-0" data-id="element-1744">
                    {question}
                  </Button>)}
              </div>
            </CardContent>
          </Card> : <div className="h-[600px]" data-id="element-1745">
            <TranscriptChat transcript={transcript} transcriptId={transcriptId} initialQuestion={initialQuestion} data-id="element-1746" />
          </div>}
      </div>

      {/* Mobile: Compact widget with conversation indicator */}
      <div className="md:hidden" data-id="element-1747">
        <Card className="cursor-pointer hover:border-primary/50 active:scale-[0.98] transition-all" onClick={() => handleWidgetClick()} data-id="element-1748">
          <CardContent className={hasConversation ? 'p-4' : 'p-5'} data-id="element-1749">
            {hasConversation ?
          // Compact version with conversation preview
          <div className="flex items-center justify-between" data-id="element-1750">
                <div className="flex-1 min-w-0" data-id="element-1751">
                  <div className="flex items-center gap-2 mb-1" data-id="element-1752">
                    <MessageSquare size={16} className="text-primary flex-shrink-0" data-id="element-1753" />
                    <span className="font-semibold text-sm" data-id="element-1754">Continue chat</span>
                    <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full" data-id="element-1755">
                      {messageCount}
                    </span>
                  </div>
                  {lastPreview && <p className="text-xs text-muted-foreground line-clamp-1 ml-6" data-id="element-1756">
                      {lastPreview}
                    </p>}
                </div>
                <ChevronRight size={18} className="text-muted-foreground flex-shrink-0 ml-2" data-id="element-1757" />
              </div> :
          // Full widget for new conversations
          <>
                <div className="flex items-center justify-between mb-3" data-id="element-1758">
                  <h3 className="font-semibold text-base flex items-center gap-2" data-id="element-1759">
                    <MessageSquare size={18} className="text-primary" data-id="element-1760" />
                    Chat about this transcript
                  </h3>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground bg-primary/10 px-2 py-1 rounded-full" data-id="element-1761">
                    <Sparkles size={12} className="text-primary" data-id="element-1762" />
                    <span className="text-[10px]" data-id="element-1763">AI</span>
                  </div>
                </div>

                <p className="text-sm text-muted-foreground mb-3" data-id="element-1764">
                  Try asking:
                </p>

                {/* Horizontal scrolling chips for mobile */}
                <div className="flex gap-2 overflow-x-auto pb-2 -mx-5 px-5 scrollbar-hide" data-id="element-1765">
                  {EXAMPLE_QUESTIONS.slice(0, 3).map((question, index) => <Button key={index} variant="outline" size="sm" onClick={e => {
                e.stopPropagation();
                handleWidgetClick(question);
              }} className="text-xs rounded-full h-8 hover:bg-primary/10 hover:text-primary hover:border-primary/50 whitespace-nowrap flex-shrink-0" data-id="element-1766">
                      {question}
                    </Button>)}
                </div>
              </>}
          </CardContent>
        </Card>

        {/* Bottom Sheet (only renders when open) */}
        {isSheetOpen && <TranscriptChatSheet transcript={transcript} transcriptId={transcriptId} initialQuestion={initialQuestion} onClose={handleSheetClose} data-id="element-1767" />}
      </div>
    </>;
};